/**
 * RBAC-PHI Type Definitions
 * Role-Based Access Control for Protected Health Information
 *
 * This module defines the authorization model for PHI access,
 * combining user roles with PHI classification levels to determine
 * what data each user can access, encrypt, and decrypt.
 */

import { UserRole } from './user.types';
import { PHIClassification } from './phi.types';

// Re-export PHIClassification for consumers of this module
export { PHIClassification } from './phi.types';

/**
 * PHI Access Operations
 * Different operations that can be performed on PHI data
 */
export enum PHIAccessOperation {
  VIEW = 'view',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  ENCRYPT = 'encrypt',
  DECRYPT = 'decrypt',
  EXPORT = 'export',
  SHARE = 'share',
}

/**
 * PHI Access Policy
 * Defines what PHI classifications a role can access
 */
export interface PHIAccessPolicy {
  role: UserRole;
  allowedClassifications: PHIClassification[];
  allowedOperations: PHIAccessOperation[];
  requiresAudit: boolean;
  requiresJustification?: boolean;
  maxRetentionDays?: number;
}

/**
 * PHI Access Request
 * Request to access PHI data with context
 */
export interface PHIAccessRequest {
  userId: string;
  userRole: UserRole;
  operation: PHIAccessOperation;
  classification: PHIClassification;
  resourceType: string;
  resourceId: string;
  justification?: string;
  purpose?: 'treatment' | 'payment' | 'operations' | 'emergency';
}

/**
 * PHI Access Decision
 * Result of RBAC authorization check
 */
export interface PHIAccessDecision {
  allowed: boolean;
  reason?: string;
  requiresJustification?: boolean;
  auditRequired: boolean;
  restrictions?: string[];
}

/**
 * Role-based PHI classification access matrix
 * Defines which roles can access which PHI classifications
 */
export const PHI_ACCESS_MATRIX: Record<UserRole, PHIAccessPolicy> = {
  admin: {
    role: 'admin',
    allowedClassifications: [
      PHIClassification.DIRECT_IDENTIFIER,
      PHIClassification.QUASI_IDENTIFIER,
      PHIClassification.CONTACT_INFO,
      PHIClassification.FINANCIAL_INFO,
      PHIClassification.HEALTH_INFO,
      PHIClassification.NON_PHI,
    ],
    allowedOperations: [
      PHIAccessOperation.VIEW,
      PHIAccessOperation.CREATE,
      PHIAccessOperation.UPDATE,
      PHIAccessOperation.DELETE,
      PHIAccessOperation.ENCRYPT,
      PHIAccessOperation.DECRYPT,
      PHIAccessOperation.EXPORT,
      PHIAccessOperation.SHARE,
    ],
    requiresAudit: true,
    requiresJustification: false,
    maxRetentionDays: 2555, // 6 years per HIPAA
  },
  doctor: {
    role: 'doctor',
    allowedClassifications: [
      PHIClassification.DIRECT_IDENTIFIER,
      PHIClassification.QUASI_IDENTIFIER,
      PHIClassification.CONTACT_INFO,
      PHIClassification.FINANCIAL_INFO,
      PHIClassification.HEALTH_INFO,
      PHIClassification.NON_PHI,
    ],
    allowedOperations: [
      PHIAccessOperation.VIEW,
      PHIAccessOperation.CREATE,
      PHIAccessOperation.UPDATE,
      PHIAccessOperation.ENCRYPT,
      PHIAccessOperation.DECRYPT,
      PHIAccessOperation.EXPORT,
    ],
    requiresAudit: true,
    requiresJustification: false,
    maxRetentionDays: 2555,
  },
  nurse: {
    role: 'nurse',
    allowedClassifications: [
      PHIClassification.QUASI_IDENTIFIER,
      PHIClassification.CONTACT_INFO,
      PHIClassification.HEALTH_INFO,
      PHIClassification.NON_PHI,
    ],
    allowedOperations: [
      PHIAccessOperation.VIEW,
      PHIAccessOperation.CREATE,
      PHIAccessOperation.UPDATE,
      PHIAccessOperation.ENCRYPT,
      PHIAccessOperation.DECRYPT,
    ],
    requiresAudit: true,
    requiresJustification: true, // Nurses need justification for PHI access
    maxRetentionDays: 90, // Shorter retention for limited access
  },
  medical_assistant: {
    role: 'medical_assistant',
    allowedClassifications: [
      PHIClassification.QUASI_IDENTIFIER,
      PHIClassification.CONTACT_INFO,
      PHIClassification.NON_PHI,
    ],
    allowedOperations: [
      PHIAccessOperation.VIEW,
      PHIAccessOperation.CREATE,
      PHIAccessOperation.UPDATE,
    ],
    requiresAudit: true,
    requiresJustification: true,
    maxRetentionDays: 90, // Limited retention
  },
  receptionist: {
    role: 'receptionist',
    allowedClassifications: [
      PHIClassification.CONTACT_INFO,
      PHIClassification.NON_PHI,
    ],
    allowedOperations: [
      PHIAccessOperation.VIEW,
      PHIAccessOperation.CREATE,
    ],
    requiresAudit: true,
    requiresJustification: true,
    maxRetentionDays: 30, // Minimal retention
  },
  assistant: {
    role: 'assistant',
    allowedClassifications: [
      PHIClassification.CONTACT_INFO,
      PHIClassification.NON_PHI,
    ],
    allowedOperations: [
      PHIAccessOperation.VIEW,
      PHIAccessOperation.CREATE,
    ],
    requiresAudit: true,
    requiresJustification: true,
    maxRetentionDays: 30,
  },
};

/**
 * Emergency override configuration
 * Allows temporary elevated access in emergencies
 */
export interface EmergencyAccess {
  userId: string;
  patientId: string;
  reason: string;
  startTime: Date;
  duration: number; // minutes
  elevatedRole: UserRole;
  approvedBy?: string;
}

/**
 * PHI de-identification levels
 * For creating safe datasets for different roles
 */
export enum DeIdentificationLevel {
  FULL = 'full', // All identifiers removed
  LIMITED = 'limited', // Some quasi-identifiers retained
  NONE = 'none', // No de-identification
}

/**
 * Maps roles to required de-identification levels
 */
export const ROLE_DEIDENTIFICATION_MAP: Record<UserRole, DeIdentificationLevel> = {
  admin: DeIdentificationLevel.NONE,
  doctor: DeIdentificationLevel.NONE,
  nurse: DeIdentificationLevel.LIMITED,
  medical_assistant: DeIdentificationLevel.LIMITED,
  receptionist: DeIdentificationLevel.FULL,
  assistant: DeIdentificationLevel.FULL,
};

/**
 * Time-based access restrictions
 * Defines when certain roles can access PHI
 */
export interface TimeBasedAccess {
  role: UserRole;
  allowedHours?: { start: number; end: number }; // 24-hour format
  allowedDays?: number[]; // 0-6 (Sunday-Saturday)
  requiresOnCall?: boolean;
  sessionTimeout: number; // minutes
}

/**
 * Default session timeouts by role (in minutes)
 */
export const ROLE_SESSION_TIMEOUTS: Record<UserRole, number> = {
  admin: 480, // 8 hours — admin sessions are long-running
  doctor: 480, // 8 hours — doctors have long clinical sessions with recording
  nurse: 240, // 4 hours
  medical_assistant: 240, // 4 hours
  receptionist: 60, // 1 hour
  assistant: 240, // 4 hours
};